import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { usePageSEO } from "../lib/seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, Users, Mail, Crown, UserMinus, Trash2, Plus, CheckCircle, XCircle, Clock } from "lucide-react";
import { motion } from "framer-motion";

export default function TeamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageSEO({ title: "Team", noIndex: true });
  const [team, setTeam] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadTeam = async () => {
    try {
      const { data } = await api.get("/team");
      setTeam(data.team);
      if (data.team) {
        const { data: inv } = await api.get("/team/invites");
        setInvites(inv);
      }
    } catch (e) { console.error("Team load failed:", e); }
    setLoading(false);
  };

  useEffect(() => { loadTeam(); }, []);

  const createTeam = async () => {
    if (!teamName.trim()) return;
    setCreating(true); setError("");
    try {
      await api.post("/team", { name: teamName.trim() });
      setMessage("Team created!");
      setTeamName("");
      await loadTeam();
    } catch (err) { setError(err.response?.data?.detail || "Failed to create team"); }
    finally { setCreating(false); }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true); setError(""); setMessage("");
    try {
      await api.post("/team/invite", { email: inviteEmail.trim() });
      setMessage(`Invite sent to ${inviteEmail}`);
      setInviteEmail("");
      await loadTeam();
    } catch (err) { setError(err.response?.data?.detail || "Failed to send invite"); }
    finally { setInviting(false); }
  };

  const removeMember = async (memberId, name) => {
    if (!window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await api.delete(`/team/members/${memberId}`);
      setMessage(`${name} removed`);
      await loadTeam();
    } catch (err) { setError(err.response?.data?.detail || "Failed to remove member"); }
  };

  const leaveTeam = async () => {
    if (!window.confirm("Leave this team? You'll lose access to shared blueprints.")) return;
    try {
      await api.post("/team/leave");
      setTeam(null);
      setMessage("You've left the team");
    } catch (err) { setError(err.response?.data?.detail || "Failed to leave team"); }
  };

  const deleteTeam = async () => {
    if (!window.confirm("Delete this team? All members will be removed. This cannot be undone.")) return;
    try {
      await api.delete("/team");
      setTeam(null);
      setMessage("Team deleted");
    } catch (err) { setError(err.response?.data?.detail || "Failed to delete team"); }
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0033CC]" /></div>;

  const isOwner = team && String(team.owner_id) === String(user?._id);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="team-page">
      <header className="backdrop-blur-2xl bg-white/80 border-b border-slate-200/60 sticky top-0 z-50 px-6 md:px-12">
        <div className="max-w-3xl mx-auto flex items-center h-16">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0033CC] font-['Outfit'] transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" />Dashboard
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A] mb-1">Team</h1>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-8">Share blueprints across your organization.</p>
        </motion.div>

        {/* Messages */}
        {message && <div className="bg-emerald-50 border border-emerald-200 p-3 mb-6 flex items-center gap-2" data-testid="team-success-msg"><CheckCircle className="w-4 h-4 text-emerald-600" /><p className="text-sm text-emerald-700">{message}</p></div>}
        {error && <div className="bg-red-50 border border-red-200 p-3 mb-6 flex items-center gap-2" data-testid="team-error-msg"><XCircle className="w-4 h-4 text-red-600" /><p className="text-sm text-red-700">{error}</p></div>}

        {!team ? (
          /* Create Team */
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 p-8 text-center" data-testid="create-team-section">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mb-2">Create a Team</h2>
            <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mb-6 max-w-md mx-auto">
              Teams let you share blueprints across your organization. Invite members and they'll see all team blueprints on their dashboard.
            </p>
            <div className="max-w-sm mx-auto flex gap-2">
              <Input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Team name (e.g. Acme Corp)" className="rounded-none border-slate-300 h-10 text-sm" data-testid="team-name-input"
                onKeyDown={e => e.key === "Enter" && createTeam()} />
              <Button onClick={createTeam} disabled={creating || !teamName.trim()} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-6 font-['Outfit'] text-sm whitespace-nowrap" data-testid="create-team-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Team"}
              </Button>
            </div>
          </motion.div>
        ) : (
          /* Team Management */
          <div className="space-y-6">
            {/* Team Info */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-slate-200 p-6" data-testid="team-info">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-['Outfit'] font-medium text-[#0F172A]">{team.name}</h2>
                  <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{team.members?.length || 0} member{(team.members?.length || 0) !== 1 ? "s" : ""}</p>
                </div>
                {isOwner ? (
                  <Button onClick={deleteTeam} variant="outline" className="rounded-none h-8 text-xs font-['Outfit'] border-red-200 text-red-600 hover:bg-red-50" data-testid="delete-team-btn">
                    <Trash2 className="w-3 h-3 mr-1.5" />Delete Team
                  </Button>
                ) : (
                  <Button onClick={leaveTeam} variant="outline" className="rounded-none h-8 text-xs font-['Outfit'] border-slate-300 text-slate-600" data-testid="leave-team-btn">
                    Leave Team
                  </Button>
                )}
              </div>

              {/* Members */}
              <div className="space-y-2">
                {(team.members || []).map((m, i) => (
                  <div key={`m-${i}`} className="flex items-center gap-3 p-3 bg-slate-50" data-testid={`team-member-${i}`}>
                    <div className="w-8 h-8 bg-[#0033CC] text-white flex items-center justify-center text-xs font-['JetBrains_Mono'] uppercase">
                      {(m.name || m.email || "?")[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-['Outfit'] font-medium text-[#0F172A] truncate">{m.name || m.email}</p>
                      <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {m.role === "owner" ? (
                        <span className="flex items-center gap-1 text-[10px] font-['JetBrains_Mono'] text-amber-600 bg-amber-50 px-2 py-0.5"><Crown className="w-3 h-3" />Owner</span>
                      ) : (
                        <span className="text-[10px] font-['JetBrains_Mono'] text-slate-400 bg-slate-100 px-2 py-0.5">{m.role}</span>
                      )}
                      {isOwner && m.role !== "owner" && (
                        <button onClick={() => removeMember(m.user_id, m.name || m.email)} className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`remove-member-${i}`}>
                          <UserMinus className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Invite */}
            {isOwner && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="bg-white border border-slate-200 p-6" data-testid="invite-section">
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3">Invite Member</h3>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@company.com"
                      className="rounded-none border-slate-300 h-10 text-sm" data-testid="invite-email-input"
                      onKeyDown={e => e.key === "Enter" && inviteMember()} />
                  </div>
                  <Button onClick={inviteMember} disabled={inviting || !inviteEmail.trim()} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-10 px-5 font-['Outfit'] text-sm" data-testid="send-invite-btn">
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1.5" />Send Invite</>}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Pending Invites */}
            {invites.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                className="bg-white border border-slate-200 p-6" data-testid="pending-invites">
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3">Invites</h3>
                <div className="space-y-2">
                  {invites.map((inv, i) => (
                    <div key={`inv-${i}`} className="flex items-center gap-3 p-2 border border-slate-100" data-testid={`invite-${i}`}>
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      <p className="text-sm text-[#0F172A] font-['IBM_Plex_Sans'] flex-1">{inv.email}</p>
                      <span className={`text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 ${inv.status === "accepted" ? "text-emerald-600 bg-emerald-50" : "text-amber-600 bg-amber-50"}`}>
                        {inv.status === "accepted" ? "Accepted" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
